/**
 * widgetRouterStore — Zustand store backing the public widget SDK.
 *
 * NOT exported from src/widgets/index.js. Consumers (charts, summary
 * surfaces) access this through `useWidgetSDK()`. Reaching into this
 * file from outside `src/widgets/` is forbidden by the ESLint rule
 * configured in package.json.
 *
 * State is intentionally tiny for MVP: one active widget at a time.
 * If we need queued widgets or multiple simultaneously, that's a
 * post-MVP enhancement and will require revisiting the SDK shape.
 */

import { create } from 'zustand';

const useWidgetRouterStore = create((set) => ({
  active: null,

  openWidget: (kind, context, opts) => {
    if (!kind || !context || typeof context !== 'object') {
      // Defensive: never crash a chart click. The SDK contract is
      // documented in src/widgets/README.md; misuse should be loud
      // in dev but harmless in prod.
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('[widgets] openWidget called with invalid args', { kind, context });
      }
      return;
    }
    set({ active: { kind, context, opts: opts || {} } });

    // Best-effort telemetry — dispatch a window event that any
    // analytics subscriber (PostHog wrapper, dev-tools panel, future
    // workflow-log forwarder) can pick up. No backend coupling here;
    // staying ahead of the analytics integration so call sites never
    // need to be revisited when telemetry actually ships.
    try {
      window.dispatchEvent(new CustomEvent('arogya:widgets:opened', {
        detail: {
          kind,
          visitId: context?.visitId || null,
          source: opts?.source || 'unknown',
          placement: opts?.placement || null,
          hasPrefill: !!(
            context?.prefill
            && typeof context.prefill === 'object'
            && Object.keys(context.prefill).length > 0
          ),
          anatomicalKind: context?.anatomicalRef?.kind || null,
          at: new Date().toISOString(),
        },
      }));
    } catch (_e) { /* analytics must not affect UX */ }
  },

  closeWidget: () => set({ active: null }),
}));

export default useWidgetRouterStore;
